import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { ZeosFontSize } from "../../services/helpers/ThemeHelpers";
import { pageConfigBySlug } from "../../services/nav/NavigationService";
import {
  getSearchParamNumber,
  qsMaker,
} from "../../services/nav/QueryStringHelpers";
import { truncatePropertyName } from "../../services/helpers/CommonHelpers";
import {
  ICompanyClaims,
  IPropertyClaims,
} from "../../services/zeos-api/contracts/Account";

interface IProps {
  properties?: IPropertyClaims[];
  activeCompany?: ICompanyClaims;
  label: string;
  navigateOnSelect?: boolean;
  isPropertyCategory?: boolean;
  enableNavigateToPortfolio?: boolean;
  enableNavigateToProperty?: boolean;
  propertyCategoryName?: string;
  tailLabel?: string;
  onPropertySelect?: (propertyId: number) => void;
  isOpen?: boolean;
  setIsOpen?: (isOpen: boolean) => void;
  selectedPropertyId?: number;
}

// Only include company/property when a real id is known - an empty "company="
// parses back as 0 (a valid finite number), which looks up a real-but-wrong company.
const buildLocationQuery = (
  propertyId?: number | null,
  companyId?: number | null
) => {
  const params = new URLSearchParams();
  if (companyId != null) params.set("company", String(companyId));
  if (propertyId != null) params.set("property", String(propertyId));
  return params.toString();
};

const formatPropertyName = (name: string) => {
  if (!name) return "";
  if (name.toLowerCase() === "ev") return "EV";
  if (name.toLowerCase() === "dr") return "DR";
  return name
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

// Short switcher names; each maps to exactly one dashboard tile (4112). The slug is the
// route segment (/dashboard/:category) and must not change with the label.
const CATEGORY_OPTIONS = [
  { label: "Building", slug: "building" }, // Building Performance
  { label: "PowerGen", slug: "solar" }, // Power Generation
  { label: "EV", slug: "ev" }, // EV Charging
  { label: "Carbon", slug: "carbon" }, // Carbon Emissions
  { label: "Battery", slug: "battery" }, // Battery Status
  { label: "DR", slug: "dr" }, // Demand Response
];

export const PropertyDropdown = (props: IProps) => {
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // Properties list from props or active company
  const properties = useMemo(
    () => props.properties || props.activeCompany?.properties || [],
    [props.properties, props.activeCompany?.properties]
  );

  const [searchParams] = useSearchParams();
  const urlPropertyId = getSearchParamNumber("property", searchParams);
  const urlCompanyId = getSearchParamNumber("company", searchParams);

  // Keep ?company= on outbound links when the URL lost it, but only fall back to
  // the active company if it owns the property - otherwise leave it off so the
  // destination resolves the company from the property instead.
  const resolveCompanyId = (propertyId?: number | null) => {
    if (urlCompanyId != null) return urlCompanyId;
    const ownsProperty = props.activeCompany?.properties?.some(
      (p) => p.propertyId === propertyId
    );
    return ownsProperty ? props.activeCompany?.companyId : undefined;
  };

  // Internal state for controlled/uncontrolled dropdown
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [internalSelectedId, setInternalSelectedId] = useState<number | null>(
    () =>
      props.navigateOnSelect
        ? null
        : urlPropertyId ?? properties[0]?.propertyId ?? null
  );

  const isOpen = props.isOpen ?? internalIsOpen;
  const setIsOpen = props.setIsOpen ?? setInternalIsOpen;
  const selectedPropertyId = props.selectedPropertyId ?? internalSelectedId;

  // Effective property based on navigation mode
  const effectivePropertyId = props.navigateOnSelect
    ? urlPropertyId ?? null
    : selectedPropertyId;
  // Fall back to the first property only when none is named. A named property missing from
  // the list (e.g. one the user cannot access, bug 4020) must not borrow another's name.
  const selectedProperty =
    properties.find((p) => p.propertyId === effectivePropertyId) ??
    (effectivePropertyId == null ? properties[0] : undefined);
  const displayName =
    truncatePropertyName(selectedProperty?.propertyName) ??
    (properties.length > 0 ? "Select a property" : "No Properties Available");

  // Property category dropdown options
  const { category: routeCategory } = useParams();
  const categoryFromRoute = (slug?: string) =>
    (CATEGORY_OPTIONS.find((o) => o.slug === slug) ?? CATEGORY_OPTIONS[0]).label;
  const [selectedCategory, setSelectedCategory] = useState(
    categoryFromRoute(routeCategory)
  );

  // Sync internal selected property from URL or first property
  useEffect(() => {
    if (!properties.length) return;
    if (!props.navigateOnSelect) {
      setInternalSelectedId(urlPropertyId ?? properties[0].propertyId);
    }
  }, [properties, props.navigateOnSelect, urlPropertyId]);

  useEffect(() => {
    if (!props.propertyCategoryName) return;
    setSelectedCategory(categoryFromRoute(routeCategory));
  }, [props.propertyCategoryName, routeCategory]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [setIsOpen]);

  // Property selection handler
  const handlePropertySelect = (propertyId: number) => {
    if (props.selectedPropertyId === undefined)
      setInternalSelectedId(propertyId);
    setIsOpen(false);
    props.onPropertySelect?.(propertyId);

    if (props.navigateOnSelect) {
      const query = buildLocationQuery(propertyId, resolveCompanyId(propertyId));
      navigate(
        `/${pageConfigBySlug("dashboard")?.slug || "dashboard"}${
          query ? `?${query}` : ""
        }`,
        {
          replace: false,
        }
      );
    }
  };

  const handleCategorySelect = (category: { label: string; slug: string }) => {
    setSelectedCategory(category.label);
    setIsOpen(false);
    const propertyId = selectedPropertyId ?? urlPropertyId;
    const query = buildLocationQuery(propertyId, resolveCompanyId(propertyId));
    navigate(
      `/${
        pageConfigBySlug("dashboard")?.slug || "dashboard"
      }/${category.slug}${query ? `?${query}` : ""}`,
      {
        replace: false,
      }
    );
  };

  // Navigation helpers
  const navigateToPortfolio = () =>
    navigate(`/${pageConfigBySlug("portfolio")?.slug || "portfolio"}`, {
      replace: false,
    });
  const navigateToProperty = () => {
    const propertyId = selectedPropertyId ?? urlPropertyId;
    const query = buildLocationQuery(propertyId, resolveCompanyId(propertyId));
    navigate(
      `/${pageConfigBySlug("dashboard")?.slug || "dashboard"}${
        query ? `?${query}` : ""
      }`,
      { replace: false }
    );
  };
  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  return (
    <div className="text-zeos-black">
      <div className={`flex items-center gap-2 text-${ZeosFontSize.xxl} mb-1`}>
        {props.enableNavigateToPortfolio ? (
          <div
            onClick={navigateToPortfolio}
            className="text-zeos-black cursor-pointer hover:text-zeos-orange-dk transition-colors"
          >
            {props.label}
          </div>
        ) : (
          <span className="text-zeos-black">{props.label}</span>
        )}

        <span className="text-md mx-2">{">"}</span>

        {props.isPropertyCategory ? (
          <div className="flex items-center">
            {/* Selected Property */}
            <div
              onClick={navigateToProperty}
              className="inline-flex items-center text-zeos-black hover:text-zeos-orange-dk cursor-pointer transition-colors"
            >
              {formatPropertyName(truncatePropertyName(selectedProperty?.propertyName) ?? "Property")}
            </div>

            {/* Category Dropdown */}
            {props.propertyCategoryName && (
              <>
                <span className="mx-2">{">"}</span>
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={handleToggle}
                    className="hover:text-zeos-orange-dk inline-flex items-center"
                  >
                    <div className="">{selectedCategory}</div>
                    <div className={`text-${ZeosFontSize.xs} ml-1`}>▼</div>
                  </button>

                  {isOpen && (
                    <div
                      className={`absolute w-max z-10 mt-1 bg-white border rounded shadow text-${ZeosFontSize.lg}`}
                    >
                      {CATEGORY_OPTIONS.map((category) => (
                        <div
                          key={category.slug}
                          onClick={() => handleCategorySelect(category)}
                          className="px-4 py-2 hover:bg-zeos-orange-dk cursor-pointer"
                        >
                          {category.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={handleToggle}
              className="group inline-flex items-center"
            >
              <span className="text-zeos-black group-hover:text-zeos-orange-dk transition-colors">
                <div className="">{displayName}</div>
              </span>
              <span
                className={`ml-2 text-${ZeosFontSize.xl} group-hover:text-zeos-orange-dk transition-colors`}
              >
                ▼
              </span>
            </button>
            {isOpen && (
              <div
                className={`absolute w-max z-10 mt-1 bg-white border rounded shadow text-${ZeosFontSize.lg}`}
              >
                {properties.length ? (
                  properties.map((p) => (
                    <div
                      key={p.propertyId}
                      onClick={() => handlePropertySelect(p.propertyId)}
                      className="px-4 py-2 hover:bg-zeos-orange-dk cursor-pointer"
                    >
                      {p.propertyName}
                    </div>
                  ))
                ) : (
                  <div className="px-4 py-2 text-gray-500">
                    No properties available
                  </div>
                )}
              </div>
            )}

            {props.tailLabel && (
              <>
                <span className="mx-2">{">"}</span>
                <span>{props.tailLabel}</span>
              </>
            )}
          </div>
        )}
      </div>

      <div className="h-[2px] w-6 bg-zeos-orange-dk mt-1"></div>

      {props.enableNavigateToProperty && (
        <div className="mt-2">
          <div
            onClick={navigateToProperty}
            className="hover:text-zeos-orange-dk inline-flex items-center cursor-pointer text-sm"
          >
            View Property →
          </div>
        </div>
      )}
    </div>
  );
};
